import { useMemo, useState } from "react";
import styles from "../styles/runner.module.css";
import type { MatchConfig } from "../types";

interface Props {
  prompt: string;
  config: MatchConfig;
  passingScore: number;
  onScored: (result: {
    score: number;
    passed: boolean;
    raw: { matched: Array<{ left: string; right: string }> };
  }) => void;
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function Match({ prompt, config, passingScore, onScored }: Props) {
  const lefts = useMemo(
    () => config.pairs.map((p) => p.left),
    [config.pairs]
  );
  const rights = useMemo(
    () => (config.shuffle ? shuffle(config.pairs.map((p) => p.right)) : config.pairs.map((p) => p.right)),
    [config.pairs, config.shuffle]
  );

  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const [pairs, setPairs] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);

  function pickLeft(l: string) {
    if (submitted) return;
    setSelectedLeft((curr) => (curr === l ? null : l));
  }

  function pickRight(r: string) {
    if (submitted || !selectedLeft) return;
    setPairs((curr) => {
      const next = { ...curr };
      // Remove any prior mapping of this right to a different left.
      for (const [k, v] of Object.entries(next)) {
        if (v === r) delete next[k];
      }
      next[selectedLeft] = r;
      return next;
    });
    setSelectedLeft(null);
  }

  function submit() {
    if (submitted) return;
    if (Object.keys(pairs).length !== lefts.length) return;
    let correct = 0;
    for (const pair of config.pairs) {
      if (pairs[pair.left] === pair.right) correct += 1;
    }
    const s = lefts.length === 0 ? 0 : correct / lefts.length;
    const ok = s >= passingScore;
    setScore(s);
    setPassed(ok);
    setSubmitted(true);
    onScored({
      score: s,
      passed: ok,
      raw: { matched: Object.entries(pairs).map(([left, right]) => ({ left, right })) },
    });
  }

  return (
    <div className={styles.col}>
      <h2 className={styles.prompt}>{prompt}</h2>

      <div className={styles.grid2}>
        <div className={styles.col}>
          <div className={styles.bucketTitle}>Conceptos</div>
          {lefts.map((l) => {
            const isSelected = selectedLeft === l;
            const matchedRight = pairs[l];
            const expectedRight = config.pairs.find((p) => p.left === l)?.right;
            const isCorrect = submitted && matchedRight === expectedRight;
            const isWrong = submitted && matchedRight && matchedRight !== expectedRight;
            const cls = [
              styles.dragItem,
              isSelected ? styles.dragItemActive : "",
              isCorrect ? styles.optionCorrect : "",
              isWrong ? styles.optionWrong : "",
            ].join(" ");
            return (
              <button
                key={l}
                type="button"
                className={cls}
                onClick={() => pickLeft(l)}
                disabled={submitted}
              >
                {l}
                {matchedRight && (
                  <span style={{ display: "block", fontSize: 12, color: "#6b7280" }}>
                    → {matchedRight}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className={styles.col}>
          <div className={styles.bucketTitle}>Opciones</div>
          {rights.map((r) => {
            const usedByLeft = Object.entries(pairs).find(([, v]) => v === r)?.[0];
            const cls = [
              styles.dragItem,
              usedByLeft ? styles.optionSelected : "",
            ].join(" ");
            return (
              <button
                key={r}
                type="button"
                className={cls}
                onClick={() => pickRight(r)}
                disabled={submitted || !selectedLeft}
              >
                {r}
                {usedByLeft && (
                  <span style={{ display: "block", fontSize: 12, color: "#6b7280" }}>
                    ← {usedByLeft}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {!submitted ? (
        <button
          type="button"
          className={styles.primaryButton}
          disabled={Object.keys(pairs).length !== lefts.length}
          onClick={submit}
        >
          Validar parejas
        </button>
      ) : (
        <div className={passed ? styles.summary : `${styles.summary} ${styles.summaryFail}`}>
          <span className={styles.scoreBadge}>{Math.round(score * 100)}%</span>
          <strong>{passed ? "¡Bien hecho!" : "Revisa las parejas"}</strong>
          <span>Mínimo requerido: {Math.round(passingScore * 100)}%</span>
        </div>
      )}
    </div>
  );
}
